// The signed-in account every mock surface answers as, plus the date helpers
// the whole family builds its ISO timestamps from — `at` for a value a screen
// prints as a DATE, `sinceMs` for one it prints as an AGE, `inMs` for a
// DEADLINE it counts down to.
//
// Extracted from `mocks.ts` so a resource module can name the session WITHOUT
// importing back into the dispatcher that imports it — `emailRoutes` needs
// `me.email`, and inlining the address instead would put one fact in two files,
// which is how the two start disagreeing about who is signed in.

import { Role, scopeCeilingForRole } from '@developerz/domain';
import type { Me, MembershipView } from './session';

/** Every fixture timestamp, so a mock date is never a bare string literal. */
export const at = (day: string): string => `${day}T12:00:00.000Z`;

// A second is a whole unit two screens print: `clock()` renders `hh:mm:ss` for
// the live feeds and `since()` renders `12s` under a minute. It is here so a
// wall-clock fixture can differ from its neighbour BELOW the minute — four
// offsets that agree on the seconds print four stamps ending in the same `:ss`.
export const SEC_MS = 1_000;
export const MIN_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

/**
 * A fixture instant a screen renders as an AGE, expressed as the age itself.
 * THE ONE relative-fixture helper — `overview-counts-mocks.ts` had a second copy
 * of this line, which is how half of Home's clock stayed current while the other
 * half rotted.
 *
 * `at()` pins a calendar instant, which is right for a value printed as a DATE
 * and wrong for one printed through `since()`/`ago()` (`lib/time.ts`): the
 * rendered string is then a function of the wall clock, so a fixture written
 * against "51d" reads "52d" the next day and is wrong forever after. That is not
 * hypothetical — `at('2026-07-19')` under the `Oldest waiting 51d` sentinel in
 * `testing/axe-cases-org.tsx` turned `main` red at 2026-09-08T23:30Z, the instant
 * `since`'s double rounding (`round(round(mins/60)/24)`) crossed 1235.5 hours.
 * `sinceMs(51 * 24 * HOUR_MS)` renders `51d` at every instant instead.
 *
 * Called PER REQUEST, never hoisted into a module-level literal: a relative
 * fixture frozen at import time is an absolute one again, and drifts as soon as
 * the process — or a dev browser tab left open — outlives a day. `inMs` below is
 * its future twin and carries the SAME rule: BOTH helpers are called per
 * request, never at module load.
 *
 * PASS A WHOLE NUMBER OF THE UNIT THE SCREEN WILL PRINT. `since` rounds at every
 * step (`round(round(round(secs/60)/60)/24)`), so an age parked near a bucket
 * edge — `90 * MIN_MS`, say — prints `2h` in one render and `1h` in the next as
 * the seconds tick past the halfway mark, and two renders of one screen disagree.
 * `6 * HOUR_MS` has half an hour of slack on either side.
 *
 * `testing/clock-drift.test.tsx` is the guard, in two halves, because #3498 took
 * the number out of the sentinel that used to carry this: it re-renders every
 * axe case whose sentinel still names an age (none today) at simulated clocks,
 * and it pins the approvals age itself as the same string at +0 and +400 days.
 * It also pins the MOCK PLANE's own fixtures — the connected PAT's expiry stays
 * in the future, and the roster's and the queue's ages read the same, at +0 and
 * +400 days.
 */
export const sinceMs = (ms: number): string => new Date(Date.now() - ms).toISOString();

/**
 * A fixture instant in the FUTURE, expressed as an offset from the caller's
 * clock — the twin of `sinceMs`, for the DEADLINE a screen counts down to (a
 * PAT's expiry, a timed hold) rather than one it dates.
 *
 * `at()` pins a calendar instant, and a calendar instant in the future is a
 * claim about the wall clock: `at('2027-01-01')` reads "in 2 months" today and
 * "337 days ago" once the clock is 400 days on, so the card it feeds claims a
 * live credential expired. `inMs(30 * DAY_MS)` reads "in 30 days" at every
 * instant, because the deadline is thirty days from NOW.
 *
 * Called PER REQUEST, never hoisted into a module-level literal — the SAME rule
 * `sinceMs` carries and for the same reason: a relative fixture frozen at
 * import time is an absolute one again. BOTH helpers are called per request,
 * never at module load; a module-level `sinceMs(...)` or `inMs(...)` is a
 * calendar instant wearing a relative costume.
 */
export const inMs = (ms: number): string => new Date(Date.now() + ms).toISOString();

/**
 * `?mock_invited=1` — the person the access-request story just invited, on
 * their FIRST sign-in (arc 101 slice 01, `e2e/access-requests.spec.ts`):
 * approved (the accept stamped `approved_at`), NOT staff, one owner membership
 * on a personal account and nothing else. Same door convention as
 * `?mock_signed_out` / `?mock_unpaid`; `billing-mocks.ts` answers the seats
 * side of the same story.
 */
export const meInvited: Me = {
  id: 'acc_invited',
  kind: 'user',
  github_login: 'grace-hopper',
  email: 'grace@hopper.dev',
  tier: 'pro',
  onboarding_state: 'pending',
  oss_verified: false,
  created_at: at('2026-07-22'),
  approved: true,
  // A GitHub sign-in: the personal GitHub-token surface serves it (#7426).
  sso_pinned: false,
  active_account_id: 'acc_invited',
  role: 'owner',
  is_platform_admin: false,
  user: {
    id: 'usr_invited',
    github_user_id: 4343,
    login: 'grace-hopper',
    email: 'grace@hopper.dev',
    avatar: null,
  },
  memberships: [
    {
      account_id: 'acc_invited',
      github_login: 'grace-hopper',
      kind: 'user',
      role: 'owner',
      reachable: true,
      signIn: null,
      sso: null,
    },
  ],
};

export const me = {
  id: 'acc_devsample',
  kind: 'org',
  github_login: 'developerz-ai',
  email: 'din@developerz.ai',
  tier: 'pro',
  onboarding_state: 'active',
  oss_verified: true,
  created_at: at('2026-01-12'),
  // Past the closed-beta gate, so `PUBLIC_MOCKS` keeps rendering the app rather
  // than the waitlist wall — and the field is exercised in dev either way.
  approved: true,
  // A GitHub sign-in, so the GitHub-token page renders in mock mode (#7426).
  sso_pinned: false,
  // Identity + org switcher (slice 02): a user who belongs to three accounts.
  active_account_id: 'acc_devsample',
  role: 'owner',
  // Dev-only: staff flag so the admin radar (/admin?host=admin) is previewable
  // in mock mode. Inert on the app host (the admin nav is host-gated, not this).
  is_platform_admin: true,
  user: {
    id: 'usr_dev',
    github_user_id: 4242,
    login: 'din',
    email: 'din@developerz.ai',
    avatar: null,
  },
  memberships: [
    // #466: the org has its own IdP this user has not linked yet, so the
    // Connected logins page offers to link it.
    {
      account_id: 'acc_devsample',
      github_login: 'developerz-ai',
      kind: 'org',
      role: 'owner',
      reachable: true,
      signIn: null,
      sso: { slug: 'developerz-ai', linked: false },
    },
    {
      account_id: 'acc_personal',
      github_login: 'din',
      kind: 'user',
      role: 'owner',
      reachable: true,
      signIn: null,
      sso: null,
    },
    // #466: an org this session is not signed in to (its SSO pins it), so the
    // switcher routes it through its own sign-in.
    {
      account_id: 'acc_acme',
      github_login: 'acme-corp',
      kind: 'org',
      role: 'admin',
      reachable: false,
      signIn: { kind: 'sso', url: '/auth/org/acme-corp/login' },
      sso: { slug: 'acme-corp', linked: true },
    },
  ] satisfies MembershipView[],
};

/**
 * `?mock_member=1` — the same org seen by a MEMBER, not its owner: the person
 * the Notifications page was rebuilt for. Same door convention as
 * `?mock_invited` / `?mock_signed_out`; `notification-settings-mocks.ts`
 * answers the settings side of the same story (nothing locked, urgent email
 * off, the member's own address).
 */
export const meMember: Me = {
  ...me,
  email: 'sam@developerz.ai',
  role: 'member',
  // What a member's session publishes: the member ceiling, the list every
  // scope gate reads (`lib/scope-gate.ts`, #7426) — so the mock member sees
  // what a real one does, not the fail-open view of a list-less `/v1/me`.
  scopes: [...scopeCeilingForRole(Role.Member)],
  is_platform_admin: false,
  user: {
    id: 'usr_member',
    github_user_id: 4444,
    login: 'sam',
    email: 'sam@developerz.ai',
    avatar: null,
  },
  memberships: [
    {
      account_id: 'acc_devsample',
      github_login: 'developerz-ai',
      kind: 'org',
      role: 'member',
      reachable: true,
      signIn: null,
      sso: null,
    },
  ],
};
